import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { Badge } from '@/components/ui/badge'
import { Label } from '@/components/ui/label'
import type { ForensicsEnvironment } from '@/types/entities'

interface ForensicsViewDialogProps {
    open: boolean
    onOpenChange: (open: boolean) => void
    environment: ForensicsEnvironment | null
}

export function ForensicsViewDialog({ open, onOpenChange, environment }: ForensicsViewDialogProps) {
    if (!environment) return null

    const runningVersions = environment.objectVersions?.filter(v => v.status === 'running' || v.status === 'pending') ?? []

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[600px] max-h-[80vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>{environment.name}</DialogTitle>
                </DialogHeader>

                <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <Label className="text-xs text-muted-foreground">Security Group</Label>
                            <p className="text-sm font-mono">{environment.securityGroupId}</p>
                        </div>
                        <div>
                            <Label className="text-xs text-muted-foreground">AWS Account</Label>
                            <p className="text-sm font-mono">{environment.awsAccountId}</p>
                        </div>
                        <div>
                            <Label className="text-xs text-muted-foreground">Status</Label>
                            <div>
                                <Badge variant={environment.status === 'active' ? 'default' : 'secondary'}>
                                    {environment.status}
                                </Badge>
                            </div>
                        </div>
                        <div>
                            <Label className="text-xs text-muted-foreground">Vault ID</Label>
                            <p className="text-sm">{environment.vaultId}</p>
                        </div>
                    </div>

                    {environment.description && (
                        <div>
                            <Label className="text-xs text-muted-foreground">Description</Label>
                            <p className="text-sm">{environment.description}</p>
                        </div>
                    )}

                    {/* Servers */}
                    <div>
                        <Label className="text-xs text-muted-foreground">Servers ({environment.servers?.length ?? 0})</Label>
                        {environment.servers && environment.servers.length > 0 ? (
                            <div className="mt-1 space-y-1">
                                {environment.servers.map((s) => (
                                    <div key={s.id} className="flex items-center gap-2 text-sm p-2 bg-muted rounded">
                                        <span className="font-mono">{s.computeObjectId}</span>
                                        {s.computeObjectVersionId && (
                                            <Badge variant="outline">v{s.computeObjectVersionId}</Badge>
                                        )}
                                        <Badge variant="secondary">{s.status}</Badge>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <p className="text-sm text-muted-foreground mt-1">No servers</p>
                        )}
                    </div>

                    {/* Running Versions */}
                    <div>
                        <Label className="text-xs text-muted-foreground">Running Object Versions ({runningVersions.length})</Label>
                        {runningVersions.length > 0 ? (
                            <div className="mt-1 space-y-1">
                                {runningVersions.map((v) => (
                                    <div key={v.id} className="flex items-center gap-2 text-sm p-2 bg-muted rounded">
                                        <span className="font-mono">{v.objectId}</span>
                                        <Badge variant="outline">v{v.objectVersionId}</Badge>
                                        <Badge variant={v.status === 'running' ? 'default' : 'secondary'}>{v.status}</Badge>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <p className="text-sm text-muted-foreground mt-1">No running versions</p>
                        )}
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    )
}
